import { objectWithoutProperties as _objectWithoutProperties, slicedToArray as _slicedToArray, extends as _extends } from '../../../_virtual/_rollupPluginBabelHelpers.js';
import React__default, { useRef, useCallback, useEffect } from 'react';
import { Popover as Popover$1 } from '@walmart-web/livingdesign-components';
import { throttle } from '../../utility/throttle.js';
import { applyCommonProps } from '../../utility/commonProps.js';
import styles from './Popover.module.scss.js';

var _excluded = ["content", "position", "children", "triggerRef", "isOpen", "hasNubbin"];
var POSITION_UPDATE_THROTTLE = 100;
var GAP = 4;
var POSITIONS = ['bottomCenter', 'bottomRight', 'bottomLeft', 'topCenter', 'topLeft', 'topRight', 'left', 'right'];
/**
 * Popover component
 *
 * ###Content
 *
 * Popovers are overlays that display contextual content on demand.
 *
 * - {@link https://livingdesign.walmart.com/components/popover/ Guidelines}
 * - {@link https://livingdesign.walmart.com/components/popover/react/ React documentation}
 *
 * ```tsx
 * const ref = React.useRef(null);
 * const [isOpen, setIsOpen] = React.useState(false);
 * <Popover
 *  onClose={() => setIsOpen(false)}
 *  triggerRef={ref}
 *  hasNubbin
 *  content={getContent()}
 *  position='left'
 *  isOpen={isOpen}
 * >
 *  <Link ref={ref} onClick={()=> setIsOpen(!isOpen)}><span>Popover at left.</span></Link>
 * </Popover>
 *```
 * */

var getOppositePosition = function getOppositePosition(position) {
  var oppositeMap = {
    bottomCenter: 'topCenter',
    bottomRight: 'topRight',
    bottomLeft: 'topLeft',
    topCenter: 'bottomCenter',
    topRight: 'bottomRight',
    topLeft: 'bottomLeft',
    left: 'right',
    right: 'left'
  };
  return oppositeMap[position] || position;
};
var PopoverComponent = function PopoverComponent(props) {
  var _applyCommonProps = applyCommonProps(props, Popover),
    content = _applyCommonProps.content,
    position = _applyCommonProps.position,
    children = _applyCommonProps.children,
    triggerRef = _applyCommonProps.triggerRef,
    isOpen = _applyCommonProps.isOpen,
    hasNubbin = _applyCommonProps.hasNubbin,
    rest = _objectWithoutProperties(_applyCommonProps, _excluded);
  if (!position) return null;
  var _React$useState = React__default.useState(position),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    currentPlacement = _React$useState2[0],
    setCurrentPlacement = _React$useState2[1];
  var _React$useState3 = React__default.useState({
      x: 0,
      y: 0
    }),
    _React$useState4 = _slicedToArray(_React$useState3, 2),
    transform = _React$useState4[0],
    setTransform = _React$useState4[1];
  var popoverRef = useRef(null);
  var internalTriggerRef = useRef(null);
  var effectiveTriggerRef = triggerRef || internalTriggerRef;
  var calculatePosition = useCallback(function () {
    var _effectiveTriggerRef$, _popoverRef$current;
    if (!(effectiveTriggerRef !== null && effectiveTriggerRef !== void 0 && effectiveTriggerRef.current) || !(popoverRef !== null && popoverRef !== void 0 && popoverRef.current)) return;
    var triggerRect = effectiveTriggerRef === null || effectiveTriggerRef === void 0 || (_effectiveTriggerRef$ = effectiveTriggerRef.current) === null || _effectiveTriggerRef$ === void 0 ? void 0 : _effectiveTriggerRef$.getBoundingClientRect();
    var popoverRect = popoverRef === null || popoverRef === void 0 || (_popoverRef$current = popoverRef.current) === null || _popoverRef$current === void 0 ? void 0 : _popoverRef$current.getBoundingClientRect();
    if (!triggerRect || !popoverRect) return;
    var viewportWidth = typeof window !== 'undefined' && window.innerWidth ? window.innerWidth : 0;
    var viewportHeight = typeof window !== 'undefined' && window.innerHeight ? window.innerHeight : 0;
    var checkPlacement = function checkPlacement(position) {
      var top = 0,
        left = 0;
      switch (position) {
        case 'bottomCenter':
          top = triggerRect.bottom + GAP;
          left = triggerRect.left + (triggerRect.width - popoverRect.width) / 2;
          break;
        case 'bottomRight':
          top = triggerRect.bottom + GAP;
          left = triggerRect.right - popoverRect.width;
          break;
        case 'bottomLeft':
          top = triggerRect.bottom + GAP;
          left = triggerRect.left;
          break;
        case 'topCenter':
          top = triggerRect.top - popoverRect.height - GAP;
          left = triggerRect.left + (triggerRect.width - popoverRect.width) / 2;
          break;
        case 'topRight':
          top = triggerRect.top - popoverRect.height - GAP;
          left = triggerRect.right - popoverRect.width;
          break;
        case 'topLeft':
          top = triggerRect.top - popoverRect.height - GAP;
          left = triggerRect.left;
          break;
        case 'left':
          top = triggerRect.top + (triggerRect.height - popoverRect.height) / 2;
          left = triggerRect.left - popoverRect.width - GAP;
          break;
        case 'right':
          top = triggerRect.top + (triggerRect.height - popoverRect.height) / 2;
          left = triggerRect.right + GAP;
          break;
        default:
          top = 0;
          left = 0;
      }
      var isValid = top >= 0 && left >= 0 && top + popoverRect.height <= viewportHeight && left + popoverRect.width <= viewportWidth;
      return {
        isValid: isValid,
        position: {
          left: left,
          top: top
        }
      };
    };
    var placementResult = checkPlacement(position);
    var bestPlacement = position;
    var bestPosition = placementResult.position;
    if (!placementResult.isValid) {
      var opposite = getOppositePosition(position);
      placementResult = checkPlacement(opposite);
      if (placementResult.isValid) {
        bestPlacement = opposite;
        bestPosition = placementResult.position;
      } else {
        for (var _i = 0, _POSITIONS = POSITIONS; _i < _POSITIONS.length; _i++) {
          var pos = _POSITIONS[_i];
          placementResult = checkPlacement(pos);
          if (placementResult.isValid) {
            bestPlacement = pos;
            bestPosition = placementResult.position;
            break;
          }
        }
      }
    }
    setCurrentPlacement(bestPlacement);
    setTransform({
      x: bestPosition.left,
      y: bestPosition.top
    });
  }, [position, effectiveTriggerRef]);
  var updatePosition = useCallback(throttle(calculatePosition, POSITION_UPDATE_THROTTLE), [calculatePosition]);
  useEffect(function () {
    if (isOpen) {
      calculatePosition();
      window.addEventListener('resize', updatePosition);
    }
    return function () {
      window.removeEventListener('resize', updatePosition);
      setCurrentPlacement(position);
      setTransform({
        x: 0,
        y: 0
      });
    };
  }, [isOpen, position, updatePosition, effectiveTriggerRef]);
  return /*#__PURE__*/React__default.createElement(Popover$1, _extends({
    triggerRef: triggerRef,
    position: currentPlacement,
    content: /*#__PURE__*/React__default.createElement("span", {
      ref: popoverRef,
      className: styles.contentPopover,
      style: {
        transform: "translate(".concat(transform.x, "px,").concat(transform.y, "px)")
      }
    }, content),
    hasNubbin: hasNubbin,
    isOpen: isOpen,
    className: styles.DVPopover
  }, rest), children);
};
PopoverComponent.displayName = 'Popover';
var Popover = /*#__PURE__*/React__default.memo(PopoverComponent);

export { Popover };
